// The retainer screen — where the money for an ongoing engagement lives.
//
// The client pays for the engagement as a WHOLE, so the P&L resolves here: this
// retainer's invoices minus the costs of every topic beneath it. The topics list
// below shows each topic's cost and nothing else, because a topic's share of this
// revenue is not a number that exists — the payment was never split that way.
//
// A topic that bills its OWN income (a brokerage margin — the melaveh malka event
// with a photographer charged on) is marked, and its cost is NOT in this retainer's
// profit: it nets against that job's own revenue on the job screen.
//
// A ONE-OFF PROJECT OPENS HERE TOO (migration 067, at /projects/:id). It is the same
// engagement — the same tiles, jobs, costs, brokerage and agreements — billed one fixed
// amount for its whole term, so the only lines that differ are the billing ones in the
// meta card: its price and what is left to bill, instead of a monthly figure and a count
// of monthly invoices.
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAsync from '../../hooks/useAsync';
import { useLookups } from '../../context/LookupsContext';
import { retainersApi } from '../../api';
import he from '../../i18n/he';
import { issuerName } from '../../utils/issuer';
import PageHeader from '../../components/PageHeader';
import AsyncBoundary from '../../components/Loader';
import StatTile from '../../components/StatTile';
import StatusBadge from '../../components/StatusBadge';
import Button from '../../components/Button';
import { formatMoney } from '../../components/Money';
import MoneyStatus from '../../components/MoneyStatus';
import { TermLine } from '../../components/RetainerTerm';
import { CostNotes, NetOfRecoveredNote } from '../../components/ExpenseSplit';
import AgreementsSection from '../../components/AgreementsSection';
import WorkFilesSection from '../../components/WorkFilesSection';
import RetainerModal from '../Retainers/RetainerModal';
import RetainerStatusModal from '../Retainers/RetainerStatusModal';
import TopicsPanel from './TopicsPanel';
import BrokerageSection from './BrokerageSection';
import DeleteRetainerModal from './DeleteRetainerModal';
import ChargesCard from './ChargesCard';
import { isProject, engagementListPath, engagementStrings } from '../../components/engagement';

const Meta = ({ retainer }) => {
  const lookups = useLookups();
  const term = retainer.retainer;
  const project = isProject(retainer);
  return (
    <div className="card w-section w-meta">
      <div className="w-meta-row">
        <span className="muted">{he.jobs.client}: <strong>{retainer.client_name}</strong></span>
        <span className="muted">{he.jobs.stream}: <strong>{lookups.streamLabel(retainer.revenue_stream)}</strong></span>
        {/* The DEFAULT issuer, labelled as such: each invoice records who actually issued it. */}
        <span className="muted">{he.jobs.defaultIssuer}: <strong>{issuerName(retainer.issuing_partner_name)}</strong></span>
        <StatusBadge kind="retainer" value={retainer.status} />
      </div>
      {!project && term.monthly != null && (
        <p className="muted">{he.retainers.monthlyAmount}: <strong>{formatMoney(term.monthly)}</strong></p>
      )}
      {/* The status rides along so the countdown stays addressed to a live engagement. */}
      <TermLine term={term} status={retainer.status} />
      {/* A count of monthly invoices means nothing on a project, which bills in whatever
          instalments were agreed — its progress is the price and what is left of it. */}
      {!project && (
        <p className="muted">
          {he.jobs.issuedProgress}:{' '}
          <strong>{term.issued}{term.months != null ? ` / ${term.months}` : ''}</strong>
        </p>
      )}
      {/* What has been invoiced is the money line below — only what is left is said here. */}
      {term.contractValue != null && (
        <p className="muted">
          {project ? he.projects.price : he.jobs.contractValue}: <strong>{formatMoney(term.contractValue)}</strong>
          {term.remaining > 0 && (
            <>{' · '}{he.jobs.remainingToInvoice}: <strong>{formatMoney(term.remaining)}</strong></>
          )}
        </p>
      )}
      {term.complete && (
        <p><span className="badge amber">{project ? he.projects.fullyBilled : he.jobs.retainerComplete}</span></p>
      )}
      {retainer.shares && retainer.shares.length > 0 && (
        <p className="muted">
          {he.jobs.shares}: {retainer.shares.map((s) => `${s.partner_name} ${s.share_pct}%`).join(' · ')}
        </p>
      )}
    </div>
  );
};

export default function RetainerDetail() {
  const { id } = useParams();
  return <RetainerView key={id} id={id} />;
}

function RetainerView({ id }) {
  const navigate = useNavigate();
  const { data: retainer, loading, refreshing, error, reload } = useAsync(() => retainersApi.get(id), [id]);
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  return (
    <AsyncBoundary loading={loading} error={error}>
      {retainer && (
        <>
          <PageHeader title={retainer.title}>
            <Button variant="subtle" onClick={() => setEditing(true)}>{he.common.edit}</Button>
            {/* Its own button beside edit, not a field inside it: ending an engagement is
                a decision with consequences worth stating (the dialog states them), while
                edit is for correcting what the retainer says. The same dialog opens from
                the retainers list. */}
            <Button variant="subtle" onClick={() => setClosing(true)}>
              {retainer.status === 'active'
                ? he.retainers.closeRetainerAction
                : he.retainers.reopenRetainer}
            </Button>
            <Button variant="subtle" onClick={() => setDeleting(true)}>{he.common.delete}</Button>
          </PageHeader>
          <div className={refreshing ? 'refreshing' : undefined}>
            <Meta retainer={retainer} />
            {/* THE CASH STATUS: what has come in and what is still owed, before the accrued
                tiles. The invoice history itself lives on the money screen. */}
            <MoneyStatus money={retainer.money} />
            {/* A monthly retainer's extra charges beyond its fee — and where one is added.
                Nothing on a one-off project, whose price is paid from the money screen. */}
            <ChargesCard retainer={retainer} onChange={reload} />
            <div className="grid cols-3 w-section">
              <StatTile label={he.jobs.revenue} value={formatMoney(retainer.profit.revenue)} />
              {/* The total is every cost rolled up from the topics, paid or not — the
                  figure profit is computed from. The note splits it when part is
                  still owed, and disappears when nothing is. */}
              <StatTile
                label={he.jobs.expenses}
                value={formatMoney(retainer.profit.expenses)}
                tone="cost"
                note={<CostNotes
                  paid={retainer.profit.expensesPaid}
                  unpaid={retainer.profit.expensesUnpaid}
                  toRecover={retainer.profit.expensesToRecover}
                />}
              />
              {/* THE RETAINER'S OWN PROFIT, and nothing else in it. A job's brokerage
                  margin is a different income unit and is reported below, marked — see
                  BrokerageSection for why it must never be added in here. */}
              {/* Profit is the gross figure, unchanged. The note reads it net of the
                  costs the client bears and nobody has charged on yet — money coming
                  back, so it never really reduced what this engagement made. It is a
                  reading of the figure above it, not a second version of it. */}
              <StatTile
                label={he.jobs.profit}
                value={formatMoney(retainer.profit.profit)}
                tone="green"
                note={<NetOfRecoveredNote
                  profit={retainer.profit.profit}
                  toRecover={retainer.profit.expensesToRecover}
                />}
              />
            </div>
            {/* Renders nothing at all when no job beneath this retainer bills its own
                income, which is the ordinary case. */}
            <BrokerageSection
              brokerage={retainer.brokerage}
              ownProfit={retainer.profit.profit}
              t={engagementStrings(retainer)}
            />
            <TopicsPanel retainer={retainer} topics={retainer.topics} onChange={reload} />
            {/* The contract itself, below the work it governs. It carries no money and
                feeds no figure above it — it is the paperwork behind the engagement,
                and the same card appears on the client card gathering every retainer's
                agreements at once. The retainer is known here, so the upload form never
                asks for it. */}
            <AgreementsSection ownerKind="retainer" ownerId={retainer.id} />
            {/* A retainer holds no work files of its own — a writer granted the
                engagement uploads to the TOPICS beneath it. So this gathers every
                topic's uploads into one list, which is where a manager looks first.
                It renders nothing when there are none. */}
            <WorkFilesSection
              ownerKind="retainer"
              ownerId={retainer.id}
              jobIds={(retainer.topics || []).map((t) => t.id)}
            />
          </div>
          {editing && (
            <RetainerModal
              retainer={retainer}
              onClose={() => setEditing(false)}
              onSaved={() => { setEditing(false); reload(); }}
            />
          )}
          {/* Closing keeps the record — the screen stays and simply reloads, which is
              the visible difference from the delete below. */}
          {closing && (
            <RetainerStatusModal
              retainer={retainer}
              onClose={() => setClosing(false)}
              onSaved={() => { setClosing(false); reload(); }}
            />
          )}
          {/* After the delete this screen's record no longer exists, so it cannot
              reload — it has to leave. `replace` so Back does not return to a URL that
              now 404s. */}
          {deleting && (
            <DeleteRetainerModal
              retainer={retainer}
              onClose={() => setDeleting(false)}
              onDeleted={() => navigate(engagementListPath(retainer), { replace: true })}
            />
          )}
        </>
      )}
    </AsyncBoundary>
  );
}
